/**
 * Secret Org API client — community platform (Library, workspaces, repertoire, Irene). Member
 * profiles live in Kora (`KoraClient` self routes); the one profile route here reads a member's
 * summary as the caller may see it (ORG-28).
 */
import { BaseApiClient } from '../client/base-client.js';
import { ApiClientError } from '../types.js';
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function parseIreneEvent(raw) {
    let value;
    try {
        value = JSON.parse(raw);
    }
    catch {
        throw new Error('Invalid Irene stream');
    }
    if (!isRecord(value))
        throw new Error('Invalid Irene stream');
    if (value.type === 'done')
        return { type: 'done' };
    if ((value.type === 'status' ||
        value.type === 'delta' ||
        value.type === 'gap' ||
        value.type === 'error') &&
        typeof value.text === 'string' &&
        value.text.length <= 2000) {
        return { type: value.type, text: value.text };
    }
    if (value.type === 'source' && isRecord(value.source)) {
        const source = value.source;
        if (typeof source.id === 'string' &&
            /^S\d{1,2}$/.test(source.id) &&
            (source.kind === 'library-post' ||
                source.kind === 'performer-profile' ||
                source.kind === 'mingle-sequence-share') &&
            typeof source.title === 'string' &&
            source.title.length <= 300 &&
            typeof source.url === 'string' &&
            source.url.length <= 1000 &&
            (/^https:\/\//.test(source.url) || /^\/(?!\/)/.test(source.url))) {
            return {
                type: 'source',
                source: { id: source.id, kind: source.kind, title: source.title, url: source.url }
            };
        }
    }
    throw new Error('Invalid Irene stream');
}
const WORKSPACE_SUGGESTION_BASES = new Set([
    'your-library-activity',
    'your-related-links',
    'your-other-workspaces',
    'library-search'
]);
function parseWorkspaceSuggestionRef(value) {
    if (!isRecord(value))
        return null;
    if (value.kind === 'library-discussion' &&
        Object.keys(value).length === 2 &&
        typeof value.topicId === 'number' &&
        Number.isSafeInteger(value.topicId) &&
        value.topicId > 0) {
        return { kind: 'library-discussion', topicId: value.topicId };
    }
    if (value.kind === 'performer-profile' &&
        Object.keys(value).length === 2 &&
        typeof value.profileId === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.profileId)) {
        return { kind: 'performer-profile', profileId: value.profileId };
    }
    if (value.kind === 'mingle-sequence-share' &&
        Object.keys(value).length === 2 &&
        typeof value.shareId === 'string' &&
        /^[A-Za-z0-9_-]{1,128}$/.test(value.shareId)) {
        return { kind: 'mingle-sequence-share', shareId: value.shareId };
    }
    return null;
}
/** Defence in depth, separate from the server's own validation: re-checks every field. */
function parseWorkspaceSuggestionItem(value) {
    if (!isRecord(value))
        return null;
    const ref = parseWorkspaceSuggestionRef(value.ref);
    if (typeof value.suggestionId === 'string' &&
        /^P\d{1,2}$/.test(value.suggestionId) &&
        ref &&
        typeof value.title === 'string' &&
        value.title.length <= 300 &&
        (value.description === null ||
            (typeof value.description === 'string' && value.description.length <= 600)) &&
        typeof value.sourceUrl === 'string' &&
        value.sourceUrl.length <= 1000 &&
        (/^https:\/\//.test(value.sourceUrl) || /^\/(?!\/)/.test(value.sourceUrl)) &&
        typeof value.provenance === 'string' &&
        typeof value.basis === 'string' &&
        WORKSPACE_SUGGESTION_BASES.has(value.basis) &&
        typeof value.reason === 'string' &&
        value.reason.length >= 1 &&
        value.reason.length <= 240 &&
        !/https?:/i.test(value.reason)) {
        return {
            suggestionId: value.suggestionId,
            ref,
            title: value.title,
            description: value.description === null ? null : value.description,
            sourceUrl: value.sourceUrl,
            provenance: value.provenance,
            basis: value.basis,
            reason: value.reason
        };
    }
    return null;
}
/** Re-validates the whole response shape; a server-side change to the contract fails loudly here. */
export function parseWorkspacePanelSuggestions(value) {
    const invalid = () => {
        throw new Error('Invalid workspace suggestions response');
    };
    if (!isRecord(value))
        return invalid();
    const outcome = value.outcome;
    if (outcome !== 'suggested' && outcome !== 'none' && outcome !== 'workspace_full')
        return invalid();
    if (typeof value.workspaceId !== 'string' ||
        typeof value.goal !== 'string' ||
        !Number.isInteger(value.basedOnRevision) ||
        value.basedOnRevision < 0 ||
        !Number.isInteger(value.omittedCount) ||
        value.omittedCount < 0 ||
        !Array.isArray(value.suggestions) ||
        value.suggestions.length > 5)
        return invalid();
    const suggestions = value.suggestions.map(item => parseWorkspaceSuggestionItem(item) ?? invalid());
    return {
        workspaceId: value.workspaceId,
        basedOnRevision: value.basedOnRevision,
        goal: value.goal,
        outcome,
        suggestions,
        omittedCount: value.omittedCount
    };
}
export async function readIreneSse(stream, onEvent) {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let pending = '';
    let total = 0;
    let doneEvent = false;
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            total += value.byteLength;
            if (total > 200_000)
                throw new Error('Irene stream exceeded size limit');
            pending += decoder.decode(value, { stream: true });
            pending = pending.replace(/\r\n/g, '\n');
            let boundary = pending.indexOf('\n\n');
            while (boundary >= 0) {
                if (boundary > 10_000)
                    throw new Error('Irene stream frame exceeded size limit');
                const frame = pending.slice(0, boundary);
                pending = pending.slice(boundary + 2);
                const data = frame
                    .split('\n')
                    .filter(line => line.startsWith('data: '))
                    .map(line => line.slice(6))
                    .join('\n');
                if (data) {
                    const event = parseIreneEvent(data);
                    onEvent(event);
                    if (event.type === 'done')
                        doneEvent = true;
                }
                boundary = pending.indexOf('\n\n');
            }
            if (pending.length > 10_000)
                throw new Error('Irene stream frame exceeded size limit');
        }
        if (!doneEvent)
            throw new Error('Irene stream ended before completion');
    }
    finally {
        reader.releaseLock();
    }
}
function isApiError(e) {
    return typeof e === 'object' && e !== null && 'statusCode' in e;
}
export class SecretOrgClient extends BaseApiClient {
    constructor(config) {
        super({
            ...config,
            // Preserve '' for same-origin; do not use || (empty string would fall back to localhost).
            // Last-resort default for direct `new SecretOrgClient({})` that bypasses the factory
            // (mainly tests/local). Env-aware resolution lives in createSecretOrgClient /
            // resolveSecretOrgBaseURL (KORA-39); the client layer must not own env detection.
            baseURL: config.baseURL == null ? 'http://localhost:3001' : config.baseURL
        });
    }
    async getPublicPerformerSummary(profileId) {
        return this.get(`/api/v1/performers/${encodeURIComponent(profileId)}/summary`);
    }
    async getLibrarySections() {
        const response = await this.get('/api/v1/library/sections');
        return response.sections;
    }
    async getLibraryDiscussions(page = 1, sectionId) {
        const query = new URLSearchParams({ page: String(page) });
        if (sectionId !== undefined)
            query.set('sectionId', String(sectionId));
        return this.get(`/api/v1/library/threads?${query}`);
    }
    async getLibraryDiscussion(id, page = 1) {
        return this.get(`/api/v1/library/threads/${id}?page=${page}`);
    }
    async searchLibrary(query) {
        const response = await this.get(`/api/v1/library/search?${new URLSearchParams({ q: query })}`);
        return response.results;
    }
    async getLibraryRelated(id) {
        return this.get(`/api/v1/library/threads/${id}/related`);
    }
    async streamIreneTurn(body, onEvent, signal) {
        const url = this.config.baseURL
            ? new URL('/api/v1/irene/turn', this.config.baseURL).toString()
            : '/api/v1/irene/turn';
        const token = await this.getAccessToken?.();
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'text/event-stream',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: JSON.stringify(body),
            signal,
            credentials: 'same-origin'
        });
        if (!response.ok) {
            throw new ApiClientError({
                code: response.status === 404 ? 'LIBRARY_NOT_FOUND' : 'IRENE_UNAVAILABLE',
                message: response.status === 404 ? 'Discussion not available' : 'Irene is temporarily unavailable',
                statusCode: response.status
            });
        }
        if (!response.body)
            throw new Error('Irene stream unavailable');
        await readIreneSse(response.body, onEvent);
    }
    async putLibraryRelated(id, refs) {
        return this.put(`/api/v1/library/threads/${id}/related`, { refs });
    }
    async listWorkspaces(limit = 20, cursor) {
        const query = new URLSearchParams({ limit: String(limit) });
        if (cursor)
            query.set('cursor', cursor);
        return this.get(`/api/v1/workspaces?${query}`);
    }
    async createWorkspace(title, goal) {
        return this.post('/api/v1/workspaces', { title, goal }, undefined, {
            retries: 0,
            retryDelay: 0
        });
    }
    async getWorkspace(id) {
        return this.get(`/api/v1/workspaces/${encodeURIComponent(id)}`);
    }
    async updateWorkspace(id, title, goal, expectedRevision) {
        return this.patch(`/api/v1/workspaces/${encodeURIComponent(id)}`, {
            title,
            goal,
            expectedRevision
        }, undefined, { retries: 0, retryDelay: 0 });
    }
    async addWorkspacePanel(id, ref, expectedRevision) {
        return this.post(`/api/v1/workspaces/${encodeURIComponent(id)}/panels`, {
            ref,
            expectedRevision
        }, undefined, { retries: 0, retryDelay: 0 });
    }
    async putWorkspaceLayout(id, panelIds, expectedRevision) {
        return this.put(`/api/v1/workspaces/${encodeURIComponent(id)}/layout`, {
            panelIds,
            expectedRevision
        }, undefined, { retries: 0, retryDelay: 0 });
    }
    async deleteWorkspace(id) {
        await this.delete(`/api/v1/workspaces/${encodeURIComponent(id)}`, undefined, undefined, {
            retries: 0,
            retryDelay: 0
        });
    }
    async suggestWorkspacePanels(id, expectedRevision) {
        const raw = await this.post(`/api/v1/workspaces/${encodeURIComponent(id)}/suggestions`, { expectedRevision }, undefined, { retries: 0, retryDelay: 0 });
        return parseWorkspacePanelSuggestions(raw);
    }
    async createLibraryDiscussion(body, key) {
        return this.post('/api/v1/library/threads', body, { headers: { 'Idempotency-Key': key } }, { retries: 0, retryDelay: 0 });
    }
    async replyToLibraryDiscussion(id, body, key) {
        return this.post(`/api/v1/library/threads/${id}/replies`, { body }, { headers: { 'Idempotency-Key': key } }, { retries: 0, retryDelay: 0 });
    }
    async getLibraryWriteAttempt(key) {
        return this.get(`/api/v1/library/write-attempts/${encodeURIComponent(key)}`);
    }
    async getFeed(cursor, limit = 10) {
        const query = new URLSearchParams({ limit: String(limit) });
        if (cursor)
            query.set('cursor', cursor);
        return this.get(`/api/v1/feed?${query}`);
    }
    /** ORG-31: 403 `MAGAZINE_MEMBERS_ONLY` for anyone who is not an MZvD member. */
    async getMagazineIssue() {
        const response = await this.get('/api/v1/magazine');
        return response.issue;
    }
}
